// The Workspace routes, beside Me's in cloud's router:
//
// - /<slug>: the workspace's frame (Workspace sidebar); its index, and the
//   routes the sections add under it.
// - /<slug>/settings: the index of the items (each with its sentence).
// - /<slug>/settings/...: profile, general, members, roles, invites, features
//   and spaces (the lists open what is chosen by path: /members/<userId>).
// - /new: making a workspace. /invite/<token>: accepting an invite.
//
// Paths name what is open; nothing about how it looks goes into the URL. The
// workspace's view and spaces are loaded before the frame is drawn (the sidebar
// does not grow rows after the first paint); a failure is told in the panel.
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import {
  type AnyRoute,
  createRoute,
  notFound,
  type RouteComponent,
} from "@tanstack/react-router";
import {
  type Api,
  lazyScreen,
  type Preloadable,
  withLazy,
} from "@trameria/ui-core";
import type { WorkspaceHost } from "./context";
import {
  SettingsFrame,
  WorkspaceFrame,
  WorkspaceHome,
  WorkspaceRoot,
} from "./frame";
import { slugOfPath } from "./places";
import {
  inviteQuery,
  invitesQuery,
  membersQuery,
  rolesQuery,
  spacesQuery,
  workspacesQuery,
  workspaceViewQuery,
} from "./queries";
import { Gate } from "./settings/items";

// The pages are loaded when first opened (ui-core's app/lazy.tsx).
const InvitePage = lazyScreen(() => import("./invite"), "InvitePage");
const NewWorkspacePage = lazyScreen(() => import("./new"), "NewWorkspacePage");
// The settings' pages come in one file (settings/pages.ts).
const pages = () => import("./settings/pages");
const SettingsIndex = lazyScreen(pages, "SettingsIndex");
const ProfileSettings = lazyScreen(pages, "ProfileSettings");
const GeneralSettings = lazyScreen(pages, "GeneralSettings");
const FeatureSettings = lazyScreen(pages, "FeatureSettings");
const MembersScreen = lazyScreen(pages, "MembersScreen");
const MemberDetail = lazyScreen(pages, "MemberDetail");
const RolesScreen = lazyScreen(pages, "RolesScreen");
const RoleDetail = lazyScreen(pages, "RoleDetail");
const NewRole = lazyScreen(pages, "NewRole");
const InvitesScreen = lazyScreen(pages, "InvitesScreen");
const InviteDetail = lazyScreen(pages, "InviteDetail");
const NewInvite = lazyScreen(pages, "NewInvite");
const SpacesScreen = lazyScreen(pages, "SpacesScreen");
const SpaceDetail = lazyScreen(pages, "SpaceDetail");
const NewSpace = lazyScreen(pages, "NewSpace");

interface Ctx {
  queryClient: QueryClient;
  api: Api;
}

export interface WorkspaceRouteOptions {
  host: WorkspaceHost;
  /** For pages that need an approved account (/new): redirects otherwise. */
  requireApproved(ctx: Ctx, back: string): Promise<unknown>;
  /** Signs in once in development, so /invite shows the joining button. */
  primeAccount(ctx: Ctx): Promise<unknown>;
}

/** Loads what a screen needs before it is drawn; a failure is the screen's to tell. */
const prime = (ctx: Ctx, ...queries: ReadonlyArray<{ queryKey: QueryKey }>) =>
  Promise.all(
    queries.map((q) =>
      // Each is a queryOptions of queries.ts; only its loading matters here.
      ctx.queryClient.ensureQueryData(q as never).catch(() => undefined),
    ),
  );

export function workspaceRoutes(
  root: AnyRoute,
  options: WorkspaceRouteOptions,
): AnyRoute[] {
  const { host } = options;

  const workspace = createRoute({
    getParentRoute: () => root,
    path: "$slug",
    beforeLoad: ({ params }) => {
      if (slugOfPath(`/${params.slug}`) === null) throw notFound();
    },
    loader: ({ context, params }) => {
      const c = context as Ctx;
      return prime(
        c,
        workspaceViewQuery(c.api, params.slug),
        spacesQuery(c.api, params.slug),
        workspacesQuery(c.api),
      );
    },
    component: function WorkspaceRoute() {
      const { slug } = workspace.useParams();
      return <WorkspaceRoot host={host} slug={slug} />;
    },
  });

  const main = createRoute({
    getParentRoute: () => workspace,
    id: "workspace-main",
    component: WorkspaceFrame,
  });
  const home = createRoute({
    getParentRoute: () => main,
    path: "/",
    component: WorkspaceHome,
  });
  const sectionRoutes = host.sections.flatMap((s) => s.routes?.(main) ?? []);

  const settings = createRoute({
    getParentRoute: () => workspace,
    path: "settings",
    component: SettingsFrame,
  });
  const settingsIndex = createRoute({
    getParentRoute: () => settings,
    path: "/",
    component: SettingsIndex,
  });
  const page = (path: string, component: RouteComponent) =>
    createRoute({ getParentRoute: () => settings, path, component });

  // A list beside the one open: the list's route, its index (nothing open:
  // the list is alone and draws no detail, so the index draws nothing), "new"
  // when there is one, and the one open.
  const listed = (
    path: string,
    screen: RouteComponent,
    loader: (c: Ctx, slug: string) => Promise<unknown>,
    parts: {
      param: string;
      one: RouteComponent;
      create?: RouteComponent;
    },
  ) => {
    const list = createRoute({
      getParentRoute: () => settings,
      path,
      loader: ({ context, params }) =>
        loader(context as Ctx, (params as { slug: string }).slug),
      component: screen,
    });
    return list.addChildren([
      createRoute({ getParentRoute: () => list, path: "/" }),
      ...(parts.create
        ? [
            createRoute({
              getParentRoute: () => list,
              path: "new",
              component: parts.create,
            }),
          ]
        : []),
      createRoute({
        getParentRoute: () => list,
        path: `$${parts.param}`,
        component: parts.one,
      }),
    ]);
  };

  // Items the sections add, behind the same gate as this part's admin items.
  const addedSettings = host.sections
    .flatMap((s) => s.settings ?? [])
    .map((entry) =>
      page(
        entry.id,
        // A section's page may be lazy (loaded with the route, as the others).
        withLazy(
          () => (
            <Gate id={entry.id}>
              <entry.Component />
            </Gate>
          ),
          entry.Component as Preloadable,
        ),
      ),
    );

  const settingsChildren = [
    settingsIndex,
    ...addedSettings,
    page("profile", ProfileSettings),
    page("general", GeneralSettings),
    page("features", FeatureSettings),
    listed(
      "members",
      MembersScreen,
      (c, slug) => prime(c, membersQuery(c.api, slug), rolesQuery(c.api, slug)),
      { param: "userId", one: MemberDetail },
    ),
    listed(
      "roles",
      RolesScreen,
      (c, slug) => prime(c, rolesQuery(c.api, slug)),
      {
        param: "roleId",
        one: RoleDetail,
        create: NewRole,
      },
    ),
    listed(
      "invites",
      InvitesScreen,
      (c, slug) => prime(c, invitesQuery(c.api, slug), rolesQuery(c.api, slug)),
      {
        param: "inviteId",
        one: InviteDetail,
        create: NewInvite,
      },
    ),
    listed(
      "spaces",
      SpacesScreen,
      (c, slug) => prime(c, spacesQuery(c.api, slug)),
      {
        param: "spaceId",
        one: SpaceDetail,
        create: NewSpace,
      },
    ),
  ];

  const make = createRoute({
    getParentRoute: () => root,
    path: "new",
    beforeLoad: ({ context, location }) =>
      options.requireApproved(context as Ctx, location.href),
    component: NewWorkspacePage,
  });

  const invite = createRoute({
    getParentRoute: () => root,
    path: "invite/$token",
    loader: async ({ context, params }) => {
      const c = context as Ctx;
      await options.primeAccount(c).catch(() => undefined);
      await prime(c, inviteQuery(c.api, params.token));
    },
    component: withLazy(function InviteRoute() {
      const { token } = invite.useParams();
      return <InvitePage token={token} />;
    }, InvitePage),
  });

  return [
    workspace.addChildren([
      main.addChildren([home, ...sectionRoutes]),
      settings.addChildren(settingsChildren),
    ]),
    make,
    invite,
  ];
}
